import React, { useCallback, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Sprite, SparkleBurst, RainLayer, CharacterView } from "./fx";

// DragToTarget — items lie scattered in the scene; drag each to its target.
// Wrong target: wiggle + thud + the item hops home (retryable). Correct:
// spring into place + sparkle burst + pop. Targets can swap to a "filled"
// asset (wilted -> bloom, theo_rain -> theo_happy). Done when all placed.

const DragToTarget = ({ payload, sounds, onDone }) => {
  const { items, targets } = payload;
  const stageRef = useRef(null);
  const targetRefs = useRef({});
  const doneRef = useRef(false);

  const [placed, setPlaced] = useState(() => new Set());
  const [placedByTarget, setPlacedByTarget] = useState({});
  const [dragging, setDragging] = useState(null); // { itemId, asset, x, y } stage-relative
  const [hotTarget, setHotTarget] = useState(null);
  const [wiggleTarget, setWiggleTarget] = useState(null);
  const [wiggleItem, setWiggleItem] = useState(null);
  const [burst, setBurst] = useState({ key: 0, x: 0, y: 0 });
  const [allDone, setAllDone] = useState(false);

  const isCharacterScene = targets.length === 1 && (targets[0].scale || 1) > 1.5;
  const showLabels = targets.length > 1 && new Set(targets.map((t) => t.label)).size > 1;

  const targetPosition = useCallback(
    (index, target) => {
      const count = targets.length;
      if (target.floating) {
        return { left: `${(0.5 + (index - (count - 1) / 2) * 0.3) * 100}%`, top: "27%" };
      }
      if (isCharacterScene) return { left: "50%", top: "46%" };
      const hasSide = !!payload.sideCharacter;
      const center = hasSide ? 0.64 : 0.5;
      const spacing = hasSide ? 0.26 : 0.3;
      return { left: `${(center + (index - (count - 1) / 2) * spacing) * 100}%`, top: "52%" };
    },
    [targets, isCharacterScene, payload.sideCharacter]
  );

  const itemHome = useCallback(
    (index) => {
      const count = items.length;
      const jitterX = (((index * 67) % 21) - 10) / 10 * 1.6;
      const spread = Math.min(0.22, 0.72 / Math.max(count - 1, 1));
      const left = (0.5 + (index - (count - 1) / 2) * spread) * 100 + jitterX;
      return { left: `${left}%`, top: "82%" };
    },
    [items.length]
  );

  const hitTest = useCallback((clientX, clientY) => {
    let best = null;
    let bestDist = Infinity;
    Object.entries(targetRefs.current).forEach(([id, el]) => {
      if (!el) return;
      const r = el.getBoundingClientRect();
      const inflated =
        clientX >= r.left - 16 && clientX <= r.right + 16 && clientY >= r.top - 16 && clientY <= r.bottom + 16;
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const d = Math.hypot(clientX - cx, clientY - cy);
      if (inflated && d < bestDist) {
        bestDist = d;
        best = { id, contained: true };
      } else if (!best && d < bestDist && d < r.width * 0.9) {
        bestDist = d;
        best = { id, contained: false };
      }
    });
    return best ? best.id : null;
  }, []);

  const land = useCallback(
    (item, targetId) => {
      setPlaced((prev) => new Set(prev).add(item.id));
      setPlacedByTarget((prev) => ({ ...prev, [targetId]: (prev[targetId] || 0) + 1 }));
      sounds.playPop((placed.size + 1) / Math.max(items.length, 1));
      sounds.playSparkle();

      const stage = stageRef.current?.getBoundingClientRect();
      const el = targetRefs.current[targetId];
      if (stage && el) {
        const r = el.getBoundingClientRect();
        setBurst((b) => ({ key: b.key + 1, x: r.left + r.width / 2 - stage.left, y: r.top + r.height / 2 - stage.top }));
      }

      if (placed.size + 1 >= items.length && !doneRef.current) {
        doneRef.current = true;
        setAllDone(true);
        const delay = payload.sideCharacter || payload.effect === "rain" ? 1100 : 650;
        window.setTimeout(() => onDone(), delay);
      }
    },
    [items.length, onDone, payload.sideCharacter, payload.effect, placed.size, sounds]
  );

  const handlePointerDown = useCallback(
    (event, item) => {
      if (placed.has(item.id) || allDone) return;
      event.currentTarget.setPointerCapture?.(event.pointerId);
      const stage = stageRef.current?.getBoundingClientRect();
      setDragging({
        itemId: item.id,
        asset: item.asset,
        x: stage ? event.clientX - stage.left : 0,
        y: stage ? event.clientY - stage.top : 0,
      });
    },
    [allDone, placed]
  );

  const handlePointerMove = useCallback(
    (event, item) => {
      setDragging((d) => {
        if (!d || d.itemId !== item.id) return d;
        const stage = stageRef.current?.getBoundingClientRect();
        return { ...d, x: stage ? event.clientX - stage.left : d.x, y: stage ? event.clientY - stage.top : d.y };
      });
      setHotTarget(hitTest(event.clientX, event.clientY));
    },
    [hitTest]
  );

  const handlePointerUp = useCallback(
    (event, item) => {
      const targetId = hitTest(event.clientX, event.clientY);
      setDragging(null);
      setHotTarget(null);
      if (!targetId) return;
      if (targetId === item.targetId) {
        land(item, targetId);
      } else {
        sounds.playThud();
        setWiggleTarget(`${targetId}-${Date.now()}`);
        setWiggleItem(`${item.id}-${Date.now()}`);
      }
    },
    [hitTest, land, sounds]
  );

  const targetFilled = useCallback(
    (target) => {
      const need = items.filter((i) => i.targetId === target.id).length;
      return need > 0 && (placedByTarget[target.id] || 0) >= need;
    },
    [items, placedByTarget]
  );

  const floatingSprite = useMemo(() => {
    if (!dragging) return null;
    return (
      <img
        src={`/assets/games/city-helpers/${dragging.asset}.png`}
        alt=""
        aria-hidden="true"
        draggable="false"
        className="ch-drag-floating"
        style={{ left: dragging.x, top: dragging.y }}
      />
    );
  }, [dragging]);

  return (
    <div className="ch-activity-body ch-drag">
      <div className={`ch-stage ${dragging ? "is-seeking" : ""}`} ref={stageRef}>
        {payload.effect === "rain" && <RainLayer active={!allDone} />}

        {payload.sideCharacter && (
          <div className="ch-side-character" style={{ left: "22%", top: "44%" }}>
            <CharacterView character={payload.sideCharacter} happy={allDone} />
          </div>
        )}

        {targets.map((target, index) => {
          const filled = targetFilled(target);
          const hot = hotTarget === target.id;
          const landed = placedByTarget[target.id] || 0;
          const need = items.filter((i) => i.targetId === target.id).length;
          const pos = targetPosition(index, target);
          const size = isCharacterScene ? 210 : 108 * (target.scale || 1);
          return (
            <div
              key={target.id}
              className={`ch-target ${target.floating ? "is-floating" : ""} ${hot ? "is-hot" : ""} ${
                filled ? "is-filled" : ""
              }`}
              style={{ left: pos.left, top: pos.top, "--ch-target-tint": target.tint }}
            >
              <div className="ch-target-inner" ref={(el) => (targetRefs.current[target.id] = el)}>
                {!target.floating && <div className="ch-target-glow" />}
                <motion.div
                  key={wiggleTarget && wiggleTarget.startsWith(target.id) ? wiggleTarget : target.id}
                  animate={
                    wiggleTarget && wiggleTarget.startsWith(target.id)
                      ? { x: [0, -7, 7, -5, 5, 0] }
                      : { scale: filled ? [1, 1.12, 1] : 1 }
                  }
                  transition={{ duration: 0.4 }}
                >
                  <Sprite
                    name={filled ? target.filledAsset || target.asset : target.asset}
                    style={{ width: size, height: isCharacterScene ? size * 1.25 : size }}
                  />
                </motion.div>
                {need > 1 && landed > 0 && <span className="ch-target-count">{landed}</span>}
              </div>
              {showLabels && <span className="ch-target-label">{target.label}</span>}
            </div>
          );
        })}

        {items.map((item, index) => {
          if (placed.has(item.id)) return null;
          const home = itemHome(index);
          const tilt = ((index * 47) % 25) - 12;
          const lifted = dragging?.itemId === item.id;
          return (
            <motion.div
              key={item.id}
              className="ch-drag-item"
              style={{ left: home.left, top: home.top, opacity: lifted ? 0 : 1 }}
              animate={wiggleItem && wiggleItem.startsWith(item.id) ? { x: [0, -6, 6, -4, 4, 0] } : {}}
              transition={{ duration: 0.4 }}
              onPointerDown={(e) => handlePointerDown(e, item)}
              onPointerMove={(e) => handlePointerMove(e, item)}
              onPointerUp={(e) => handlePointerUp(e, item)}
              role="button"
              tabIndex={-1}
              aria-label={item.label}
            >
              <div className="ch-item-shadow" />
              <motion.span
                style={{ display: "block" }}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 250, damping: 15, delay: 0.12 + index * 0.09 }}
              >
                <Sprite name={item.asset} style={{ transform: `rotate(${tilt}deg)`, width: 74, height: 74 }} />
              </motion.span>
            </motion.div>
          );
        })}

        {floatingSprite}
        <SparkleBurst trigger={burst.key} reach={72} style={{ left: burst.x, top: burst.y }} />
      </div>
    </div>
  );
};

export default DragToTarget;
